import React, { useEffect, useState, useCallback, useRef } from "react";
import { uploadChunked } from "../lib/upload";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { authStore } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "../components/ui/sonner";
// 旧工作日历已移除，但项目卡片的开工安排、施工阶段仍共用 CalendarDays 图标，必须保留。
import { Briefcase, FileText, FileSignature, Banknote, Camera, ClipboardCheck, Send, Upload, Loader2, CheckCircle2, Clock, XCircle, ChevronDown, ChevronRight, ImageIcon, AlertTriangle, Bell, ExternalLink, HardHat, Mic, CheckCheck, Building2, CalendarDays } from "lucide-react";
import { PortalRfqList } from "../components/PortalRfqList";
import { PortalCompanyCard } from "../components/PortalCompanyCard";
import { PortalContractSigning } from "../components/PortalContractSigning";
import { PortalPunchList } from "../components/PortalPunchList";
import { PortalSignAction, PortalStartWork, PortalTodoBanner } from "../components/PortalSubOps";
import { CompletionDocList, CompletionUploader, SignedContractLinks, EvidenceGapWarning, GapUploadButtons, focusSection, parseDeepLink, PORTAL_SECTION } from "../components/SubcontractOps";
import { PortalTaskChain } from "../components/subcontract/PortalTaskChain";
import { PortalLienBar } from "../components/portal/PortalLienBar";
import { fmtD, fmtDT } from "../lib/date";
import SubcontractorSchedule from "../components/SubcontractorSchedule";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PORTAL_MENUS, portalView, portalSectionView } from "../lib/portalNavigation";
import PortalPaymentFlow from "../components/portal/PortalPaymentFlow";
import PortalStagePaymentStatus, { usePortalPaymentRequests } from "../components/portal/PortalStagePaymentStatus";
import useSubcontractorRefresh from "../hooks/useSubcontractorRefresh";
import InspectionCorrectionInbox from "../components/subcontract/InspectionCorrectionInbox";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const money = (n) => "$" + Number(n || 0).toLocaleString();
// 与付款接口一致：只复用当前合同、当前阶段已归档且有文件的资料。
const stageDocuments = (sc, milestoneId) => (sc.completionPhotos || []).filter((p) => p.fileId && p.milestoneId === milestoneId);
const isCompletionPhoto = (p) => p.docType === "photo" || p.kind === "image" || String(p.contentType || "").startsWith("image/");

const QUOTE_BADGE = {
  submitted: { zh: "待审核", en: "Under review", cls: "bg-amber-100 text-amber-700", Icon: Clock },
  eng_review: { zh: "待工程经理审批", en: "Eng. manager review", cls: "bg-orange-100 text-orange-700", Icon: Clock },
  approved: { zh: "已通过", en: "Approved", cls: "bg-green-100 text-green-700", Icon: CheckCircle2 },
  rejected: { zh: "已驳回", en: "Rejected", cls: "bg-red-100 text-red-700", Icon: XCircle },
};
const MS_BADGE = {
  planned: { zh: "待申请", en: "Planned", cls: "bg-slate-100 text-slate-600" },
  requested: { zh: "审批中", en: "In review", cls: "bg-amber-100 text-amber-700" },
  paid: { zh: "已支付", en: "Paid", cls: "bg-green-100 text-green-700" },
};

export default function SubcontractorPortal() {
  const { lang, user, api } = useApp();
  const zh = lang === "zh";
  const [sp] = useSearchParams();
  const location = useLocation(), navigate = useNavigate();
  const view = portalView(location.pathname, location.search);
  const deepDone = useRef("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [checkins, setCheckins] = useState({});
  const [punch, setPunch] = useState([]);
  const token = authStore.get("ali_token");
  const fileUrl = (fid) => `${BACKEND}/api/files/${fid}/download?auth=${token}`;
  const loadPunch = useCallback(() => api.get("/portal/punch").then(({ data }) => setPunch(data || [])).catch(() => setPunch([])), [api]);
  const punchFor = (sc) => punch.filter((p) => (p.subcontractId ? p.subcontractId === sc.id : p.projectId === sc.projectId));
  const openTarget = useCallback((sid, section, sticky = false, signing = null) => {
    if (!sid) return;
    const menu = PORTAL_MENUS.find(m => m.key === portalSectionView(section));
    navigate({ pathname: menu.path, search: new URLSearchParams({ sub: sid, section: section || "card", ...(sticky ? { highlight: "1" } : {}), ...(signing ? { signing } : {}) }).toString() });
    setOpenId(sid);
    setTimeout(() => { focusSection(`${signing === "lien" ? "portal-lien" : signing === "change_order" ? "portal-sco-bar" : PORTAL_SECTION[section] || "portal-sub"}-${sid}`, sticky) || focusSection(`portal-sub-${sid}`, sticky); }, 200);
  }, [navigate]);

  const [notifError, setNotifError] = useState("");
  const loadNotifs = useCallback(async () => {
    try { const { data } = await api.get("/reminders"); setNotifs(data || []); setNotifError(""); } catch { setNotifError(zh ? "通知加载失败，请刷新重试" : "Unable to load notifications. Please retry."); }
  }, [api, zh]);

  const loaded = useRef(false);
  const inFlight = useRef(null);
  const reloadQueued = useRef(false);
  const [loadError, setLoadError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const load = useCallback(async () => {
    // 上传/完工可能恰好遇到后台刷新：合并并发请求后再拉一次，所有调用者等待最新一轮完成，避免旧快照吞掉资料更新。
    if (inFlight.current) { reloadQueued.current = true; return inFlight.current; }
    setRefreshing(true);
    if (!loaded.current) setLoading(true);
    // 先登记共享 Promise 再开始请求，队列结束时才释放加载态与上传按钮互锁。
    inFlight.current = Promise.resolve().then(async () => {
      try {
        do {
          reloadQueued.current = false;
          try { const { data } = await api.get("/my/subcontracts"); setRows(data || []); setLoadError(""); if (data?.[0]) setOpenId((o) => o || data[0].id); loaded.current = true; }
          catch { setLoadError(zh ? "合同加载失败，请刷新重试" : "Unable to load contracts. Please refresh."); }
        } while (reloadQueued.current);
        api.get("/portal/checkin-links").then(({ data }) => setCheckins(Object.fromEntries((data || []).map((x) => [x.subcontractId, x])))).catch(() => {});
        loadPunch();
      } finally { setLoading(false); setRefreshing(false); inFlight.current = null; }
    });
    return inFlight.current;
  }, [api, loadPunch, zh]);
  useEffect(() => { load(); loadNotifs(); }, [load, loadNotifs]);
  // 合同、通知共用可见页面刷新；切换菜单不丢表单，返回浏览器立即取最新状态。
  const refreshPortal = useCallback(() => { load(); loadNotifs(); }, [load, loadNotifs]);
  useSubcontractorRefresh(refreshPortal);
  useEffect(() => {
    window.addEventListener("sub-schedule-changed", refreshPortal);
    return () => window.removeEventListener("sub-schedule-changed", refreshPortal);
  }, [refreshPortal]);
  useEffect(() => {
    const key = location.key + location.search;
    if (deepDone.current === key || loading || !sp.get("sub")) return;
    if (!rows.some(r => r.id === sp.get("sub"))) return;
    setOpenId(sp.get("sub"));
    const timer = setTimeout(() => {
      const target = sp.get("signing") === "lien" ? "portal-lien" : sp.get("signing") === "change_order" ? "portal-sco-bar" : PORTAL_SECTION[sp.get("section")] || "portal-sub";
      const hash = (location.hash || "").slice(1);
      const plan = rows.find(row => row.id === sp.get("sub"))?.paymentPlan || [];
      const milestone = plan.find(m => hash === `portal-stage-${m.id}` || hash === `portal-pay-row-${m.id}` || (sp.get("milestone") || sp.get("ms")) === m.id);
      // 父级定位沿用具体阶段目标，避免覆盖子级展开后对旧 ms/hash 深链的定位。
      const stageTarget = milestone && `${hash.startsWith("portal-pay-row-") || sp.get("section") === "pay" ? "portal-pay-row" : "portal-stage"}-${milestone.id}`;
      focusSection(stageTarget || target + "-" + sp.get("sub"), sp.get("highlight") === "1");
      // 数据刷新可能先取消旧定时器，实际定位后才完成，避免快速刷新吞掉通知跳转。
      deepDone.current = key;
    }, 200);
    return () => clearTimeout(timer);
  }, [loading, rows, sp, location.key, location.search, location.hash, view]);

  const markRead = async (id) => {
    try { await api.post(`/reminders/${id}/read`); setNotifs((n) => n.map((x) => (x.id === id ? { ...x, read: true } : x))); } catch { /* noop */ }
  };
  const openNotif = (n) => {
    markRead(n.id);
    if (["city_inspection_correction", "city_inspection_schedule"].includes(n.type) && n.link?.startsWith("/inspection-schedule/")) {
      setShowNotifs(false); navigate(n.link); return;
    }
    if (n.type === "subcontract_dual_sign" && rows.some(r => n.link === `/subcontracts/${r.id}/signing`)) {
      setShowNotifs(false);
      navigate("/portal");
      load(); return;
    }
    // 新派工通知定位月历任务；合同通知继续打开原合同区块，保留既有处理流程。
    if (n.type === "sub_schedule" && n.link?.startsWith("/portal?")) {
      setShowNotifs(false); navigate("/portal/schedule?" + n.link.split("?")[1]); return;
    }
    const { sub, section } = parseDeepLink(n.link);
    if (sub && rows.some((r) => r.id === sub)) {
      setShowNotifs(false);
      // 旧签署通知共用 section=pay；折叠阶段后必须把对应签署入口展开并定位。
      openTarget(sub, section, true, n.type === "lien_waiver_sign" ? "lien" : n.type === "sub_change_order_sign" ? "change_order" : null);
    }
  };
  const readAllNotifs = async () => {
    try { const { data } = await api.post("/reminders/read-all", {}); setNotifs((n) => n.map((x) => ({ ...x, read: true }))); toast.success(zh ? `已将 ${data.count} 条通知标为已读` : `${data.count} marked read`); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const notifGroups = React.useMemo(() => {
    const m = new Map();
    notifs.forEach((n) => {
      const key = n.projectId || "__other";
      if (!m.has(key)) m.set(key, { key, label: n.address || rows.find((r) => r.projectId === n.projectId)?.project?.address || (zh ? "其他" : "Other"), items: [], unread: 0 });
      const g = m.get(key); g.items.push(n); if (!n.read) g.unread += 1;
    });
    return [...m.values()].sort((a, b) => b.unread - a.unread);
  }, [notifs, rows, zh]);
  const unread = notifs.filter((n) => !n.read).length;

  const uploadFiles = async (files) => {
    const out = [];
    const list = Array.from(files || []);
    for (let n = 0; n < list.length; n++) {
      const file = list[n];
      const tid = toast.loading(zh ? `正在上传照片 ${n + 1}/${list.length}：${file.name}` : `Uploading ${n + 1}/${list.length}: ${file.name}`);
      try {
        const data = await uploadChunked(api, file, { onProgress: ({ pct, state }) => toast.loading(state === "retrying" ? (zh ? `网络波动，正在重试 ${n + 1}/${list.length}：${file.name}` : `Retrying ${n + 1}/${list.length}: ${file.name}`) : state === "offline" ? (zh ? "已离线，恢复网络后自动继续" : "Offline · resumes when online") : (zh ? `正在上传 ${n + 1}/${list.length}：${file.name} · ${pct}%` : `Uploading ${n + 1}/${list.length}: ${file.name} · ${pct}%`), { id: tid }) });
        out.push({ fileId: data.fileId, filename: data.filename, contentType: data.contentType, kind: data.kind });
        toast.success(zh ? `已上传：${data.filename}` : `Uploaded: ${data.filename}`, { id: tid });
      } catch (e) {
        const detail = e?.response?.data?.detail || (e?.code === "ECONNABORTED" ? (zh ? "网络超时" : "timeout") : e?.message);
        toast.error(zh ? `照片「${file.name}」上传失败：${detail}。请检查网络后重试` : `Upload failed for ${file.name}: ${detail}`, { id: tid, duration: 8000 });
        throw e;
      }
    }
    return out;
  };

  if (loading) return <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-7 w-7 animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="subcontractor-portal">
      <div className="flex items-center gap-2">
        <Briefcase className="h-6 w-6 text-brand-500" />
        <h1 className="text-2xl font-bold text-slate-900">{zh ? "分包商工作台" : "Subcontractor Portal"}</h1>
        <span className="text-sm text-slate-400">· {zh ? user.nameZh || user.name : user.name} · {rows.length}</span>
        <div className="ml-auto relative">
          <button aria-label={zh ? "工作台通知" : "Portal notifications"} aria-expanded={showNotifs} data-testid="portal-bell" onClick={() => setShowNotifs((v) => !v)} className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-600">
            <Bell className="h-5 w-5" />
            {unread > 0 && <span data-testid="portal-bell-count" className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 text-[10px] font-bold text-white bg-red-600 rounded-full flex items-center justify-center">{unread}</span>}
          </button>
          {showNotifs && (
            <div data-testid="portal-notifs" className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] max-h-96 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg z-20 p-2">
              <p className="text-xs font-semibold text-slate-500 px-2 py-1 flex items-center justify-between gap-2">
                <span>{zh ? "通知" : "Notifications"}{unread > 0 && <span className="ml-1 text-red-600">{unread}</span>}</span>
                {unread > 0 && <button type="button" data-testid="portal-notifs-read-all" onClick={readAllNotifs} className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-500 hover:text-brand-700"><CheckCheck className="h-3.5 w-3.5" />{zh ? "全部已读" : "Mark all read"}</button>}
              </p>
              {notifError && <p role="alert" className="text-xs text-red-700 p-2">{notifError}</p>}
              {notifs.length === 0 && !notifError ? <p className="text-xs text-slate-400 px-2 py-4 text-center">{zh ? "暂无通知" : "No notifications"}</p> : notifGroups.map((g) => (
                <div key={g.key} data-testid={`portal-notif-group-${g.key}`} className="mb-1.5">
                  <p className="text-[10px] font-semibold text-slate-400 px-2 pt-1 pb-0.5 truncate flex items-center gap-1"><Building2 className="h-3 w-3" />{g.label}{g.unread > 0 && <span className="text-red-600">· {g.unread}</span>}</p>
                  {g.items.map((n) => (
                    <button key={n.id} data-testid={`portal-notif-${n.id}`} data-link={n.link || ""} onClick={() => openNotif(n)} className={`w-full text-left rounded-lg px-2.5 py-2 mb-1 text-xs ${n.read ? "bg-white text-slate-500" : "bg-red-50 text-slate-700"}`}>
                      {!n.read && <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500 mr-1.5 align-middle" />}
                      {n.message}
                      <span className="block text-[10px] text-slate-400 mt-0.5">{fmtDT(n.createdAt)}{parseDeepLink(n.link).sub && rows.some((r) => r.id === parseDeepLink(n.link).sub) ? <span className="ml-1.5 text-brand-500 font-medium">{zh ? "点击直达 →" : "Open →"}</span> : null}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 菜单有独立网址；旧通知仍按业务区块定位，三个页面共享同一合同/通知数据。 */}
      <Tabs value={view} onValueChange={key => navigate(PORTAL_MENUS.find(m => m.key === key).path)}>
        <TabsList aria-label={zh ? "分包商工作台菜单" : "Portal menu"} className="grid grid-cols-1 sm:grid-cols-3 w-full h-auto gap-1 p-1">
          {PORTAL_MENUS.map((menu, i) => <TabsTrigger key={menu.key} value={menu.key} data-testid={"portal-menu-" + menu.key} className="min-h-11 whitespace-normal gap-2 text-sm">
            {i === 0 ? <Building2 className="h-4 w-4 shrink-0" /> : i === 1 ? <CalendarDays className="h-4 w-4 shrink-0" /> : <Banknote className="h-4 w-4 shrink-0" />}{zh ? menu.zh : menu.en}
          </TabsTrigger>)}
        </TabsList>
      </Tabs>
      {(loadError || notifError) && <div role="alert" className="rounded-xl bg-red-50 p-3 text-sm text-red-700 flex items-center justify-between gap-3"><span>{loadError || notifError}</span><Button variant="outline" disabled={refreshing} onClick={refreshPortal}>{zh ? "重试" : "Retry"}</Button></div>}
      {view !== "progress" && <PortalTodoBanner rows={rows} checkins={checkins} zh={zh} onOpen={openTarget} />}
      {user.role === "subcontractor" && (view !== "progress" || !rows.length) && <InspectionCorrectionInbox api={api} zh={zh} />}

      <div hidden={view !== "contracts"} className="space-y-5" data-testid="portal-contracts-page">
        <PortalCompanyCard api={api} zh={zh} />
        <PortalContractSigning rows={rows} api={api} zh={zh} user={user} reload={load} error={loadError} refreshing={refreshing} />
        <PortalRfqList zh={zh} />
      </div>
      <div hidden={view !== "schedule"} className="space-y-5" data-testid="portal-schedule-page">
        {view === "schedule" && <SubcontractorSchedule key={user.username} onOpenStage={openTarget} />}
        {rows.filter(sc => sc.startWork?.plannedDate).map(sc => <section key={sc.id} className="rounded-xl bg-white border border-slate-200 p-4" data-testid={"portal-startwork-section-" + sc.id}>
          <h2 className="font-semibold text-sm mb-3">{sc.project?.address} · {sc.tradeName}</h2>
          <PortalStartWork sc={sc} api={api} zh={zh} reload={refreshPortal} />
        </section>)}
      </div>
      <div hidden={view === "schedule"} className="space-y-4" data-testid="portal-active-section" data-count={rows.length}>
      {rows.length === 0 && !loadError ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400" data-testid="portal-empty">{zh ? "暂无关联合同。请联系项目经理确认合同已关联到您的分包商账号。" : "No linked contracts. Ask your project manager to check the subcontractor account assignment."}</div>
      ) : (
        <>
          {rows.map((sc, index) => (
            <SubcontractCard key={sc.id} view={view} cityCorrections={index === 0 && user.role === "subcontractor"} sc={sc} open={openId === sc.id} onToggle={() => setOpenId((o) => (o === sc.id ? null : sc.id))}
              api={api} zh={zh} reload={load} uploadFiles={uploadFiles} fileUrl={fileUrl} checkin={checkins[sc.id]} punch={punchFor(sc)} reloadPunch={loadPunch} />
          ))}
        </>
      )}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid, id }) {
  return (
    <div id={id} className="scroll-mt-24 border-t border-slate-100 pt-3 mt-3" data-testid={testid}>
      <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2"><Icon className="h-4 w-4 text-red-600" />{title}</p>
      {children}
    </div>
  );
}

/** 阶段成为唯一办理入口：复用合同原来的上传、完工及申请接口。
 * 资料按 milestoneId 精确关联；没有阶段字段的历史资料和整改放在共用折叠中，避免误归期或漏掉处理入口。
 */
function SubcontractCard({ view, cityCorrections, sc, open, onToggle, api, zh, reload, uploadFiles, fileUrl, checkin, punch = [], reloadPunch }) {
  const openPunch = punch.filter((p) => p.status !== "closed" && p.status !== "submitted").length;
  const location = useLocation();
  const [activeStageId, setActiveStageId] = useState(null);
  const [sharedOpen, setSharedOpen] = useState(false);
  const query = new URLSearchParams(location.search);
  const deepSection = query.get("sub") === sc.id ? query.get("section") : null;
  const payments = usePortalPaymentRequests({ api, sc, enabled: open && view === "progress" });
  useEffect(() => {
    if (view !== "progress") return;
    const params = new URLSearchParams(location.search);
    const hash = (location.hash || "").slice(1);
    const match = (sc.paymentPlan || []).find(m => hash === `portal-stage-${m.id}` || hash === `portal-pay-row-${m.id}` || (params.get("milestone") || params.get("ms")) === m.id);
    if (params.get("sub") !== sc.id && !match) return;
    if (match) {
      if (!open) onToggle();
      setActiveStageId(match.id);
      const target = hash.startsWith("portal-pay-row-") || params.get("section") === "pay" ? `portal-pay-row-${match.id}` : `portal-stage-${match.id}`;
      const timer = setTimeout(() => focusSection(target, params.get("highlight") === "1"), 200);
      return () => clearTimeout(timer);
    }
    const section = params.get("section");
    if (["tasks", "rects", "checkin", "pay"].includes(section)) setSharedOpen(true);
    if (["stages", "pay", "completion"].includes(section)) {
      const plan = sc.paymentPlan || [];
      // 历史付款区链接也用于末笔放弃留置权签署，不能默认展开第一阶段而把签字入口藏在末阶段。
      const lienPending = sc.lien && !["signed", "void"].includes(sc.lien.status);
      const stage = (section === "pay" && (params.get("signing") === "lien" || lienPending) ? plan[plan.length - 1] : section === "pay" ? plan.find(m => m.status === "requested" || m.stageStatus === "closed") : plan.find(m => ["notified", "in_progress", "completed_pending", "inspecting"].includes(m.stageStatus))) || plan[0];
      setActiveStageId(stage?.id || null);
      if (section === "completion") setSharedOpen(true);
    }
    // 仅导航时处理旧通知和阶段锚点；后台刷新不应重新打开用户刚收起的面板。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.search, location.hash, sc.id, view]);
  const [plans, setPlans] = useState([]);
  const [rects, setRects] = useState([]);
  const [busy, setBusy] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [payPhotos, setPayPhotos] = useState([]);
  const [payNote, setPayNote] = useState("");
  const [payArchive, setPayArchive] = useState(true);
  // 共用一份付款表单，按阶段保存临时草稿，来回切换不会丢照片，也不会把另一阶段的照片带入申请。
  const payDrafts = useRef({});
  const payRef = useRef(null);
  const [quote, setQuote] = useState({ quoteAmount: sc.quotedAmount || "", expectedStart: sc.expectedStart || "", note: sc.quoteNote || "" });
  const quoteApproved = sc.quoteStatus === "approved" || (!sc.quoteStatus && sc.contractAmount);
  const quotePending = sc.quoteStatus === "submitted" || sc.quoteStatus === "eng_review";
  const esignSigned = sc.signing ? sc.signing.status === "completed" : sc.esignStatus === "signed";
  const esignSent = sc.esignStatus === "sent";
  const esignGateOk = sc.signing ? esignSigned : esignSigned || !sc.esignStatus || sc.esignStatus === "none";
  const canApplyPay = quoteApproved && esignGateOk;

  const loadPlans = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${sc.projectId}/files`); setPlans(data || []); } catch { setPlans([]); }
    try { const { data } = await api.get(`/subcontracts/${sc.id}/rectifications`); setRects(data || []); } catch { setRects([]); }
  }, [api, sc.projectId, sc.id]);
  useEffect(() => { if (open) loadPlans(); }, [open, loadPlans]);

  const submitQuote = async () => {
    if (!quote.quoteAmount || Number(quote.quoteAmount) <= 0) { toast.error(zh ? "请填写报价金额" : "Enter quote amount"); return; }
    setBusy(true);
    try {
      await api.post(`/subcontracts/${sc.id}/quote`, { quoteAmount: Number(quote.quoteAmount), expectedStart: quote.expectedStart, note: quote.note });
      toast.success(zh ? "报价已提交，等待审核" : "Quote submitted for review");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const openPay = (m) => {
    if (busy) return;
    if (payFor) payDrafts.current[payFor] = { photos: payPhotos, note: payNote, archive: payArchive };
    const draft = payDrafts.current[m.id];
    setPayFor(m.id); setPayPhotos(draft?.photos || []); setPayNote(draft?.note || ""); setPayArchive(draft?.archive ?? true);
  };
  const addPayPhotos = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try { const up = await uploadFiles(Array.from(files)); setPayPhotos((p) => [...p, ...up]); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const submitPay = async (m) => {
    if (busy) return;
    if (!payPhotos.length && !stageDocuments(sc, m.id).length) { toast.error(zh ? "请上传本阶段完工照片或验收资料" : "Add completion photos or acceptance documents for this stage"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/subcontracts/${sc.id}/payment-requests`, { milestoneId: m.id, photos: payPhotos, note: payNote, archiveAsCompletion: payArchive });
      toast.success(data?.archivedCompletion ? (zh ? `已提交进度款申请，并将 ${data.archivedCompletion} 张照片归档为「${m.name}」完工照片` : `Submitted · ${data.archivedCompletion} photo(s) archived as completion photos`) : (zh ? "已提交进度款申请，等待项目经理验收" : "Progress payment submitted"));
      setPayFor(null); setPayPhotos([]); setPayNote("");
      delete payDrafts.current[m.id];
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const uploadCompletion = async (files, docType = "photo", milestoneId = null) => {
    if (!files?.length || busy) return false;
    setBusy(true);
    try {
      const photos = (await uploadFiles(Array.from(files))).map((p) => ({ ...p, docType, ...(milestoneId ? { milestoneId } : {}) }));
      await api.post(`/subcontracts/${sc.id}/completion-photos`, { photos });
      toast.success(zh ? "竣工验收资料已提交" : "Completion documents submitted");
      await reload();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); return false; }
    finally { setBusy(false); }
  };
  const relabelDoc = async (p, docType) => {
    try { await api.patch(`/subcontracts/${sc.id}/completion-docs/${p.id}`, { docType }); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const deleteDoc = async (p) => {
    if (!window.confirm(zh ? `删除「${p.filename}」？` : `Delete ${p.filename}?`)) return;
    try { await api.delete(`/subcontracts/${sc.id}/completion-docs/${p.id}`); toast.success(zh ? "已删除" : "Deleted"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const renderPayment = (m) => {
                const b = MS_BADGE[m.status] || MS_BADGE.planned;
                const archivedDocs = stageDocuments(sc, m.id);
                return (
                  <div key={m.id} id={`portal-pay-row-${m.id}`} className="scroll-mt-24 bg-slate-50 rounded-md px-2.5 py-1.5" data-testid={`portal-pay-row-${m.id}`} data-kind={m.kind || "progress"}>
                    <div className="flex items-center gap-2 text-xs">
                      <div className="flex-1 min-w-0"><p className="text-slate-700">{m.kind === "change_order" && <span data-testid={`portal-pay-co-${m.id}`} className="mr-1 text-[9px] font-semibold px-1 py-px rounded bg-fuchsia-100 text-fuchsia-700 align-middle">{zh ? "变更单" : "CO"}</span>}{zh ? "进度款" : "Progress payment"}</p><p className="text-xs text-slate-500">{money(m.amount)}</p></div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${b.cls}`}>{zh ? b.zh : b.en}</span>
                      {m.status === "planned" && canApplyPay && payFor !== m.id && (
                        <Button data-testid={`portal-pay-btn-${m.id}`} size="sm" className="h-11 px-3 text-xs bg-brand-500 hover:bg-brand-600 text-white" disabled={busy} onClick={() => openPay(m)}><Send className="h-3 w-3 mr-1" />{zh ? "申请进度款" : "Apply"}</Button>
                      )}
                    </div>
                    <PortalStagePaymentStatus sc={sc} m={m} zh={zh} {...payments} />
                    {!canApplyPay && <p className="mt-2 text-xs text-amber-700">{!quoteApproved ? (zh ? "等待报价审核通过后申请付款。" : "Quote approval is required before requesting payment.") : (zh ? "请先在信息与合约管理完成合同签署。" : "Complete contract signing in Information & Contracts first.")}</p>}
                    {payFor === m.id && (
                      <div className="mt-3 border-t border-slate-200 pt-3 space-y-3" data-testid={`portal-pay-form-${m.id}`}>
                        <p className="text-xs text-slate-600 flex items-center gap-1" data-testid={`portal-pay-evidence-${m.id}`}><Camera className="h-3.5 w-3.5 shrink-0 text-brand-500" />{archivedDocs.length ? (zh ? `已自动关联本阶段 ${archivedDocs.length} 份资料，无需重复上传` : `${archivedDocs.length} stage document(s) linked — no need to upload again`) : (zh ? "请上传本阶段完工照片或验收资料" : "Add completion photos or acceptance documents for this stage")}</p>
                        <span data-testid={`portal-pay-existing-${m.id}`} className="sr-only">{archivedDocs.length}</span>
                        <EvidenceGapWarning gaps={sc.evidenceGaps?.[m.id]} isFinal={(sc.paymentPlan || []).slice(-1)[0]?.id === m.id} zh={zh} testid={`portal-pay-gap-${m.id}`} onGoUpload={() => focusSection(`portal-stage-docs-${m.id}`)} />
                        <GapUploadButtons gaps={sc.evidenceGaps?.[m.id]} zh={zh} busy={busy} onUpload={(fs, t) => uploadCompletion(fs, t, m.id)} testidPrefix={`portal-pay-${m.id}`} />
                        <details>
                        <summary className="min-h-11 flex items-center cursor-pointer text-xs text-slate-600">{zh ? "补充照片与说明（选填）" : "Additional photos and note (optional)"}</summary>
                        <input ref={payRef} type="file" accept="image/*" multiple disabled={busy} className="hidden" data-testid={`portal-pay-photo-input-${m.id}`} onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; addPayPhotos(fs); }} />
                        <div className="flex flex-wrap gap-2">
                          {payPhotos.map((p, i) => (
                            <div key={i} className="h-14 w-14 rounded-md overflow-hidden border border-slate-200 bg-white flex items-center justify-center">
                              {p.kind === "image" ? <img src={fileUrl(p.fileId)} alt={p.filename} className="h-full w-full object-cover" /> : <ImageIcon className="h-5 w-5 text-slate-400" />}
                            </div>
                          ))}
                          <button type="button" aria-label={zh ? "补充现场照片" : "Add site photos"} data-testid={`portal-pay-upload-${m.id}`} onClick={() => payRef.current?.click()} disabled={busy} className="h-14 w-14 rounded-md border border-dashed border-slate-300 flex items-center justify-center text-slate-400 hover:border-brand-400 focus-visible:ring-2 focus-visible:ring-brand-400">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                          </button>
                        </div>
                        <Textarea placeholder={zh ? "进度说明（选填）" : "Note (optional)"} value={payNote} onChange={(e) => setPayNote(e.target.value)} className="text-xs min-h-[40px]" data-testid={`portal-pay-note-${m.id}`} />
                        {(m.stageStatus === "closed" || (sc.paymentPlan || []).slice(-1)[0]?.id === m.id || (sc.evidenceGaps?.[m.id] || []).length > 0) && (
                          <label className="flex items-start gap-2 text-[11px] text-slate-600 cursor-pointer" data-testid={`portal-pay-archive-${m.id}`}>
                            <input type="checkbox" checked={payArchive} onChange={(e) => setPayArchive(e.target.checked)} data-testid={`portal-pay-archive-check-${m.id}`} className="mt-0.5 accent-brand-500" />
                            <span>{zh ? "将补充照片归档到本阶段" : "Archive additional photos to this stage"}</span>
                          </label>
                        )}
                        </details>
                        <div className="flex gap-2">
                          <Button data-testid={`portal-pay-submit-${m.id}`} size="sm" className="h-11 text-xs bg-brand-500 hover:bg-brand-600 text-white" disabled={busy || (!payPhotos.length && !archivedDocs.length)} onClick={() => submitPay(m)}><Send className="h-3.5 w-3.5 mr-1" />{zh ? "提交申请" : "Submit"}</Button>
                          <Button size="sm" variant="ghost" className="h-11 text-xs" disabled={busy} onClick={() => { delete payDrafts.current[m.id]; setPayFor(null); setPayPhotos([]); }}>{zh ? "取消" : "Cancel"}</Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
  };
  const qb = QUOTE_BADGE[sc.quoteStatus];

  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid={`portal-sub-${sc.id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-4 text-left">
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800"><span className="text-slate-900">{sc.project?.address}</span> <span className="text-slate-400 font-normal">· {sc.tradeName}</span></p>
          <p className="text-[11px] text-slate-400">{sc.project?.phase} · {zh ? "项目经理" : "PM"} {sc.project?.manager}</p>
          {view !== "progress" && (() => {
            const plan = sc.paymentPlan || [];
            const closed = plan.filter((m) => m.stageStatus === "closed").length;
            const paid = plan.filter((m) => m.status === "paid").reduce((a, m) => a + Number(m.amount || 0), 0);
            const pct = plan.length ? Math.round((closed / plan.length) * 100) : 0;
            return (
              <div className="mt-1 flex items-center gap-2 flex-wrap text-[10px]" data-testid={`portal-progress-${sc.id}`} data-pct={pct}>
                <span className="h-1.5 w-24 rounded-full bg-slate-100 overflow-hidden inline-block"><i className="block h-full bg-emerald-500" style={{ width: `${pct}%` }} /></span>
                <span className="text-slate-600">{zh ? `阶段 ${closed}/${plan.length} 已验收 · ${pct}%` : `${closed}/${plan.length} stages closed · ${pct}%`}</span>
                <span className="text-emerald-700">{zh ? "已收款" : "Paid"} {money(paid)}</span>
                {checkin && <span className={checkin.reportedToday ? "text-emerald-700" : "text-amber-700"}>{checkin.reportedToday ? (zh ? "今日已报工 ✓" : "Reported today ✓") : (zh ? "今日未报工" : "No check-in today")}</span>}
                {openPunch > 0 && <span data-testid={`portal-punch-badge-${sc.id}`} className="inline-flex items-center gap-0.5 rounded-full bg-red-600 text-white px-1.5 py-px font-semibold"><AlertTriangle className="h-2.5 w-2.5" />{zh ? `${openPunch} 项待整改` : `${openPunch} to fix`}</span>}
              </div>
            );
          })()}
        </div>
        {qb && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${qb.cls}`}>{zh ? qb.zh : qb.en}</span>}
        <span className="text-sm font-bold text-slate-900">{money(sc.contractAmount)}</span>
      </button>

      <div hidden={view !== "progress"} id={`portal-stages-${sc.id}`} data-testid={`portal-stages-${sc.id}`}>
        <div id={`portal-pay-${sc.id}`} data-testid={`portal-pay-${sc.id}`}><div id={`portal-completion-${sc.id}`} data-testid={`portal-completion-${sc.id}`}>
          <PortalPaymentFlow sc={sc} zh={zh} open={open} onOpen={onToggle} activeStageId={activeStageId} onStageChange={setActiveStageId} renderStage={m => (
            <div className="space-y-4">
              <StagePortalRow sc={sc} m={m} api={api} zh={zh} reload={reload} parentBusy={busy} onUploadCompletion={files => uploadCompletion(files, "photo", m.id)} />
              <div data-testid={`portal-stage-docs-${m.id}`} className="space-y-2">
                <CompletionDocList items={stageDocuments(sc, m.id)} fileUrl={fileUrl} zh={zh} testidPrefix={`portal-stage-doc-${m.id}`} onRelabel={relabelDoc} onDelete={deleteDoc} />
                <details className="rounded-lg border border-slate-200 px-3">
                  <summary className="min-h-11 flex items-center cursor-pointer text-xs text-slate-600">{zh ? "补充验收单、质保书等资料" : "Add acceptance forms, warranty or other documents"}</summary>
                  <div className="pb-3"><CompletionUploader zh={zh} busy={busy} onUpload={(files, type) => uploadCompletion(files, type, m.id)} testidPrefix={`portal-stage-cdoc-${m.id}`} camera /></div>
                </details>
              </div>
              {renderPayment(m)}
              {(sc.paymentPlan || []).slice(-1)[0]?.id === m.id && <PortalLienBar sc={sc} api={api} zh={zh} onSigned={reload} />}
            </div>
          )}>
            <details open={sharedOpen} onToggle={event => setSharedOpen(event.currentTarget.open)} className="mt-3 rounded-lg border border-slate-200 px-3" data-testid={`portal-shared-${sc.id}`}>
              <summary className="flex min-h-11 cursor-pointer items-center justify-between gap-2 text-xs font-medium text-slate-600">
                <span>{zh ? "报工、任务与共用资料" : "Daily reports, tasks & shared documents"}</span>
                {(openPunch > 0 || rects.some(r => r.status !== "resolved")) && <span className="text-amber-700">{zh ? "有待处理整改" : "Corrections pending"}</span>}
              </summary>
              <div className="pb-3">
          {quoteApproved && (
            <Section icon={Mic} title={zh ? "每日施工进展提交" : "Daily construction check-in"} testid={`portal-checkin-${sc.id}`}>
              {!checkin ? <p className="text-xs text-slate-400">…</p> : (
                <div className="flex items-center gap-3 flex-wrap text-xs">
                  <a href={checkin.url} target="_blank" rel="noreferrer" data-testid={`portal-checkin-open-${sc.id}`} className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg font-semibold text-white ${checkin.reportedToday ? "bg-slate-500" : "bg-brand-500 hover:bg-brand-600"}`}>
                    <Mic className="h-4 w-4" />{checkin.reportedToday ? (zh ? "今日已报工 · 再次提交" : "Reported today · submit again") : (zh ? "提交今日报工（语音 / 文字 / 照片）" : "Submit today's check-in")}<ExternalLink className="h-3 w-3" />
                  </a>
                  <span className="text-slate-600" data-testid={`portal-checkin-last-${sc.id}`}>
                    {checkin.lastDate ? (zh ? `最近报工 ${fmtD(checkin.lastDate)} · 到场 ${checkin.lastCrew ?? "—"} 人${checkin.lastDone ? ` · ${String(checkin.lastDone).slice(0, 60)}` : ""}` : `Last ${fmtD(checkin.lastDate)} · crew ${checkin.lastCrew ?? "—"}`) : (zh ? "尚未提交过报工" : "No check-ins yet")}
                    {checkin.days ? ` · ${zh ? `累计 ${checkin.days} 天` : `${checkin.days} days`}` : ""}
                  </span>
                  <span className="text-[10px] text-slate-400">{zh ? "到场人数 / 今日完成 / 明日计划 / 需配合事项，收工前提交；业主每日进展与 PM 看板自动同步。" : "Crew, done today, plan tomorrow, needs — before end of day."}</span>
                </div>
              )}
            </Section>
          )}

          <Section icon={HardHat} title={zh ? "施工任务明细" : "Construction task details"} testid={`portal-tasks-section-${sc.id}`}>
            <details open={deepSection === "tasks" ? true : undefined} className="group rounded-lg border border-slate-200 bg-white">
              <summary className="flex min-h-10 cursor-pointer list-none items-center justify-between gap-2 px-3 text-xs font-medium text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
                <span>{zh ? "查看关联施工任务与提交记录" : "View linked construction tasks and reports"}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 transition-transform group-open:rotate-180" />
              </summary>
              <div className="border-t border-slate-100 p-3"><PortalTaskChain sc={sc} api={api} zh={zh} reload={reload} /></div>
            </details>
          </Section>


                <Section icon={Camera} title={zh ? "未分阶段的历史资料" : "Historical documents without a stage"} testid={`portal-shared-docs-${sc.id}`}>
                  <CompletionDocList items={(sc.completionPhotos || []).filter(p => !(sc.paymentPlan || []).some(m => m.id === p.milestoneId))} fileUrl={fileUrl} zh={zh} testidPrefix={`portal-cdoc-${sc.id}`} onRelabel={relabelDoc} onDelete={deleteDoc} />
                </Section>
                <PortalScoSignBar sc={sc} api={api} zh={zh} />
          <Section icon={ClipboardCheck} title={zh ? `待整改 / 待复检${openPunch ? ` · ${openPunch}` : ""}` : `Rectifications${openPunch ? ` · ${openPunch}` : ""}`} testid={`portal-rects-${sc.id}`}>
            <PortalPunchList items={punch} zh={zh} api={api} uploadFiles={uploadFiles} fileUrl={fileUrl} reload={reloadPunch} subcontractId={sc.id} />
            {rects.length === 0 ? (punch.length === 0 && <p className="text-xs text-slate-400">{zh ? "暂无整改意见" : "No rectification items"}</p>) : (
              <div className={`space-y-2 ${punch.length ? "mt-3 pt-3 border-t border-slate-100" : ""}`}>
                {punch.length > 0 && <p className="text-[11px] text-slate-500">{zh ? "验收整改意见 & 回复" : "Inspection rectifications & replies"}</p>}
                {rects.map((rc) => <RectItem key={rc.id} rc={rc} api={api} zh={zh} reload={loadPlans} uploadFiles={uploadFiles} fileUrl={fileUrl} />)}
              </div>
            )}
          </Section>

                {cityCorrections && view === "progress" && <InspectionCorrectionInbox api={api} zh={zh} />}
              </div>
            </details>
          </PortalPaymentFlow>
        </div></div>
      </div>
      {open && (
        <div className="px-4 pb-4">
          <div hidden={view !== "contracts"}>
          {/* Approved drawings */}
          <Section icon={FileText} title={zh ? "批准图纸" : "Approved Plans"} testid={`portal-plans-${sc.id}`}>
            {plans.length === 0 ? <p className="text-xs text-slate-400">{zh ? "暂无批准图纸" : "No approved plans"}</p> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {plans.map((f) => (
                  <a key={f.id} href={fileUrl(f.fileId)} target="_blank" rel="noreferrer" data-testid={`portal-plan-${f.id}`} className="block rounded-lg border border-slate-200 overflow-hidden hover:border-red-300 bg-white">
                    <div className="relative aspect-[4/3] bg-slate-50 flex items-center justify-center">
                      <FileText className="h-6 w-6 text-red-300" />
                      {(f.kind === "pdf" || f.kind === "image") && <img src={`${BACKEND}/api/files/${f.fileId}/thumb?auth=${authStore.get("ali_token")}`} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover object-top" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                      {f.kind === "pdf" && <span className="absolute left-1 top-1 text-[9px] font-bold px-1 py-px rounded bg-red-600 text-white">PDF{f.pageCount ? ` · ${f.pageCount}${zh ? " 页" : "p"}` : ""}</span>}
                    </div>
                    <p className="px-2 py-1 text-[10px] text-slate-700 truncate">{f.filename}</p>
                  </a>
                ))}
              </div>
            )}
          </Section>

          {/* Quote + expected start */}
          <Section icon={ClipboardCheck} title={zh ? "报价 & 预计开工" : "Quote & Start"} testid={`portal-quote-${sc.id}`}>
            {sc.quoteStatus === "rejected" && <p className="text-[11px] text-red-600 mb-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{zh ? "报价被驳回，请修改后重新提交" : "Quote rejected, please resubmit"} {sc.quoteReviewNote && `· ${sc.quoteReviewNote}`}</p>}
            {quoteApproved ? (
              <p className="text-xs text-green-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />{zh ? `报价已通过，合同金额 ${money(sc.contractAmount)}，预计开工 ${sc.expectedStart || "未定"}` : `Approved: ${money(sc.contractAmount)}, start ${sc.expectedStart || "TBD"}`}</p>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">{zh ? "报价金额 ($)" : "Quote ($)"}</label>
                    <Input data-testid={`portal-quote-amount-${sc.id}`} type="number" value={quote.quoteAmount} onChange={(e) => setQuote((q) => ({ ...q, quoteAmount: e.target.value }))} className="h-8 text-sm" disabled={quotePending} />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">{zh ? "预计开工日期" : "Expected start"}</label>
                    <Input data-testid={`portal-quote-start-${sc.id}`} type="date" value={quote.expectedStart} onChange={(e) => setQuote((q) => ({ ...q, expectedStart: e.target.value }))} className="h-8 text-sm" disabled={quotePending} />
                  </div>
                </div>
                <Textarea placeholder={zh ? "备注（含材料/工期说明等）" : "Note"} value={quote.note} onChange={(e) => setQuote((q) => ({ ...q, note: e.target.value }))} className="text-sm min-h-[52px]" disabled={quotePending} />
                {quotePending ? (
                  <p className="text-[11px] text-amber-600 flex items-center gap-1" data-testid={`portal-quote-pending-${sc.id}`}><Clock className="h-3 w-3" />{sc.quoteStatus === "eng_review" ? (zh ? `已提交 ${money(sc.quotedAmount)}，项目经理已初审，等待工程部经理审批` : `Submitted ${money(sc.quotedAmount)}, awaiting construction manager`) : (zh ? `已提交 ${money(sc.quotedAmount)}，等待项目经理审核` : `Submitted ${money(sc.quotedAmount)}, awaiting PM review`)}</p>
                ) : (
                  <Button data-testid={`portal-quote-submit-${sc.id}`} size="sm" disabled={busy} className="bg-brand-500 hover:bg-brand-600 text-white text-xs h-8" onClick={submitQuote}>
                    <Send className="h-3.5 w-3.5 mr-1" />{zh ? "提交报价" : "Submit quote"}
                  </Button>
                )}
              </div>
            )}
          </Section>

          {/* 双签使用本人账号；历史已签原件仍可按原方式下载。 */}
          {(sc.signing || esignSent || esignSigned) && (
            <Section icon={FileSignature} title={zh ? "合同签署" : "Contract signing"} testid={`portal-esign-${sc.id}`}>
              {sc.signing || !esignSigned ? <PortalSignAction sc={sc} api={api} zh={zh} /> : <SignedContractLinks sc={sc} token={authStore.get("ali_token")} zh={zh} testidPrefix={`portal-esign-${sc.id}`} />}
            </Section>
          )}

          </div>
        </div>
      )}
    </div>
  );
}

function StagePortalRow({ sc, m, api, zh, reload, parentBusy, onUploadCompletion }) {
  const [d, setD] = useState({ actualStart: m.actualStart || "", expectedEnd: m.expectedEnd || "" });
  const [busy, setBusy] = useState(false);
  const photoRef = useRef(null);
  const status = m.stageStatus || "pending";
  const blocked = busy || parentBusy;
  const photoCount = stageDocuments(sc, m.id).filter(isCompletionPhoto).length;
  const uploadStagePhotos = async (files) => {
    if (!files.length || blocked) return;
    setBusy(true);
    try { await onUploadCompletion(files); }
    finally { setBusy(false); }
  };
  const act = async (path, body) => {
    if (blocked) return;
    if (path === "complete" && !photoCount) { toast.error(zh ? "请先上传本阶段完工照片" : "Upload completion photos for this stage first"); return; }
    setBusy(true);
    try { await api.post(`/subcontracts/${sc.id}/stages/${m.id}/${path}`, body || {}); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div id={`portal-stage-${m.id}`} className="scroll-mt-24 bg-slate-50 rounded-md px-2.5 py-2 text-xs" data-testid={`portal-stage-${m.id}`}>
      {status === "pending" && <p className="text-slate-500">{zh ? "等待项目经理安排开工。收到通知后，在此填写工期。" : "Awaiting the project manager’s start notice. Enter your dates here when notified."}</p>}
      {["completed_pending", "inspecting"].includes(status) && <p className="text-brand-700">{zh ? "已报完工，等待项目组验收；可继续补充资料并查看付款进度。" : "Completion reported. Awaiting project acceptance; you can add documents and track payment here."}</p>}
      {status === "closed" && <p className="text-emerald-700">{zh ? "项目组已确认完工，资料将自动用于本阶段付款申请。" : "Completion confirmed. Stage documents are linked to the payment request automatically."}</p>}
      {status === "notified" && (
        <div className="flex items-end gap-1.5 mt-1.5 flex-wrap">
          <div><label className="text-[10px] text-slate-500 block">{zh ? "实际开工" : "Start"}</label><input data-testid={`portal-stage-start-${m.id}`} type="date" value={d.actualStart} onChange={(e) => setD((x) => ({ ...x, actualStart: e.target.value }))} className="h-11 text-xs border border-slate-200 rounded px-1.5" /></div>
          <div><label className="text-[10px] text-slate-500 block">{zh ? "预计完工" : "End"}</label><input data-testid={`portal-stage-end-${m.id}`} type="date" value={d.expectedEnd} onChange={(e) => setD((x) => ({ ...x, expectedEnd: e.target.value }))} className="h-11 text-xs border border-slate-200 rounded px-1.5" /></div>
          <Button data-testid={`portal-stage-dates-${m.id}`} size="sm" className="h-11 text-xs bg-brand-500 hover:bg-brand-600 text-white" disabled={blocked} onClick={() => act("dates", d)}>{zh ? "提交工期" : "Submit"}</Button>
        </div>
      )}
      {(status === "in_progress" || status === "completed_pending" || status === "inspecting" || status === "closed") && (
        <p className="text-slate-500 mt-1">{zh ? "开工" : "Start"} {m.actualStart || "-"} · {zh ? "完工" : "End"} {m.expectedEnd || m.actualEnd || "-"}</p>
      )}
      {["pending", "notified", "in_progress", "completed_pending", "inspecting", "closed"].includes(status) && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input ref={photoRef} type="file" accept="image/*" multiple disabled={blocked} className="hidden" data-testid={`portal-stage-photos-input-${m.id}`} onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ""; uploadStagePhotos(files); }} />
          <Button type="button" data-testid={`portal-stage-photos-${m.id}`} size="sm" variant="outline" className="h-11 text-xs" disabled={blocked} onClick={() => photoRef.current?.click()}>{busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Camera className="mr-1 h-3.5 w-3.5" />}{zh ? "上传本阶段完工照片" : "Add completion photos"}</Button>
          <span className="text-xs text-slate-500" data-testid={`portal-stage-photo-count-${m.id}`}>{photoCount ? (zh ? `本阶段已上传 ${photoCount} 张照片，申请进度款时自动关联` : `${photoCount} stage photo(s) ready for the payment request`) : (zh ? "请先上传至少 1 张本阶段完工照片" : "Upload at least one completion photo for this stage")}</span>
        </div>
      )}
      {status === "in_progress" && (
        <Button data-testid={`portal-stage-complete-${m.id}`} size="sm" className="h-11 text-xs mt-2 bg-brand-500 hover:bg-brand-600 text-white" disabled={blocked || !photoCount} onClick={() => act("complete", {})}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />{zh ? "确认完工，提交验收" : "Confirm complete & request inspection"}</Button>
      )}
      {(m.companyInspection || m.municipalInspection) && (
        <p className="mt-1 text-[11px]">{m.companyInspection && <span className="text-slate-600 mr-2">🏢 {zh ? "公司检查" : "Company"} {m.companyInspection}</span>}{m.municipalInspection && <span className="text-red-600 font-semibold">🏛 {zh ? "市政府检查" : "Municipal"} {m.municipalInspection}</span>}</p>
      )}
    </div>
  );
}


/* 已批准的分包变更单：签署状态 · 在线签署链接 · PDF（变更单阶段须签署后才能申请付款） */
function PortalScoSignBar({ sc, api, zh }) {
  const [items, setItems] = useState(null);
  const hasCo = (sc.paymentPlan || []).some((m) => m.kind === "change_order");
  const planLen = sc.paymentPlan?.length;
  useEffect(() => {
    if (!hasCo) return;
    api.get(`/subcontracts/${sc.id}/change-orders`).then(({ data }) => setItems((data.items || []).filter((c) => c.status === "approved"))).catch(() => setItems([]));
  }, [api, sc.id, hasCo, planLen]);
  if (!hasCo || !items || !items.length) return null;
  const token = authStore.get("ali_token");
  return (
    <div data-testid={`portal-sco-bar-${sc.id}`} data-count={items.length} className="mb-2 space-y-1">
      {items.map((c) => (
        <div key={c.id} data-testid={`portal-sco-${c.id}`} data-sign={c.signStatus || "none"} className="flex items-center gap-2 flex-wrap rounded-md border border-fuchsia-200 bg-fuchsia-50 px-2 py-1.5 text-[11px]">
          <span className="font-mono font-semibold text-fuchsia-800">{c.number}</span>
          <span className="text-slate-700 truncate max-w-[40%]">{c.title}</span>
          <span className="font-bold text-slate-900">{money(c.approvedAmount ?? c.amount)}</span>
          {c.signStatus === "signed"
            ? <span data-testid={`portal-sco-signed-${c.id}`} className="ml-auto inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 className="h-3 w-3" />{zh ? `已签署 ${fmtDT(c.signature?.at)}` : `Signed ${fmtDT(c.signature?.at)}`}</span>
            : c.signUrl
              ? <a data-testid={`portal-sco-sign-${c.id}`} href={c.signUrl} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-700 text-white font-semibold hover:bg-fuchsia-800"><FileSignature className="h-3 w-3" />{zh ? "在线签署变更单" : "Sign change order"}</a>
              : <span className="ml-auto text-amber-700">{zh ? "等待签署链接" : "Awaiting link"}</span>}
          <a data-testid={`portal-sco-pdf-${c.id}`} href={`${BACKEND}${c.pdfUrl}?auth=${token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 underline text-slate-600 hover:text-fuchsia-700"><FileText className="h-3 w-3" />PDF</a>
        </div>
      ))}
      <p className="text-[10px] text-slate-500">{zh ? "变更单阶段须先在线签署变更单，验收后才能申请该阶段付款。" : "Sign the change order online before requesting its payment stage."}</p>
    </div>
  );
}

function RectItem({ rc, api, zh, reload, uploadFiles, fileUrl }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const imgRef = useRef(null);
  const [pending, setPending] = useState([]);
  const resolved = rc.status === "resolved";

  const addImages = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try { const up = await uploadFiles(Array.from(files)); setPending((p) => [...p, ...up]); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    if (!text.trim() && pending.length === 0) { toast.error(zh ? "请填写回复或上传图片" : "Add text or images"); return; }
    setBusy(true);
    try { await api.post(`/rectifications/${rc.id}/reply`, { text, images: pending }); setText(""); setPending([]); toast.success(zh ? "回复已提交" : "Reply sent"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className={`rounded-lg border p-2.5 ${resolved ? "border-green-200 bg-green-50/50" : "border-amber-200 bg-amber-50/40"}`} data-testid={`portal-rect-${rc.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-semibold text-slate-800">{rc.title}</span>
        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${resolved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{resolved ? (zh ? "已关闭" : "Resolved") : (zh ? "待整改" : "Open")}</span>
      </div>
      {rc.detail && <p className="text-[11px] text-slate-600 mb-1">{rc.detail}</p>}
      {(rc.images || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">{rc.images.map((im, i) => <a key={i} href={fileUrl(im.fileId)} target="_blank" rel="noreferrer" className="h-12 w-12 rounded overflow-hidden border"><img src={fileUrl(im.fileId)} alt="" className="h-full w-full object-cover" /></a>)}</div>
      )}
      {(rc.replies || []).map((rp) => (
        <div key={rp.id} className="text-[11px] bg-white rounded px-2 py-1 mt-1 border border-slate-100">
          <span className="font-medium text-slate-700">{rp.by}</span> <span className="text-slate-400">({rp.role})</span>: <span className="text-slate-600">{rp.text}</span>
          {(rp.images || []).length > 0 && <div className="flex flex-wrap gap-1 mt-1">{rp.images.map((im, i) => <a key={i} href={fileUrl(im.fileId)} target="_blank" rel="noreferrer" className="h-10 w-10 rounded overflow-hidden border"><img src={fileUrl(im.fileId)} alt="" className="h-full w-full object-cover" /></a>)}</div>}
        </div>
      ))}
      {!resolved && (
        <div className="mt-2 space-y-1.5">
          <Textarea placeholder={zh ? "整改回复…" : "Reply…"} value={text} onChange={(e) => setText(e.target.value)} className="text-xs min-h-[44px]" data-testid={`portal-rect-reply-${rc.id}`} />
          {pending.length > 0 && <p className="text-[10px] text-slate-500">{zh ? `已附 ${pending.length} 张图片` : `${pending.length} image(s) attached`}</p>}
          <div className="flex gap-2">
            <input ref={imgRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; addImages(fs); }} />
            <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy} onClick={() => imgRef.current?.click()}><ImageIcon className="h-3.5 w-3.5 mr-1" />{zh ? "回传图片" : "Add image"}</Button>
            <Button data-testid={`portal-rect-submit-${rc.id}`} size="sm" className="h-7 text-[11px] bg-brand-500 hover:bg-brand-600 text-white" disabled={busy} onClick={submit}><Send className="h-3.5 w-3.5 mr-1" />{zh ? "提交回复" : "Send"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
